"use client"
import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { fetchPricingSettings } from "../utils/auth"

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState("monthly")
  const [pricingPlans, setPricingPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch pricing settings on component mount
  useEffect(() => {
    const getPricing = async () => {
      try {
        setLoading(true)
        const data = await fetchPricingSettings()
        // Transform the data to match the expected structure
        setPricingPlans(data || [])
        setError(null)
      } catch (err) {
        console.error("Failed to fetch pricing settings:", err)
        setError("Failed to load pricing plans. Using default pricing.")
        // Use fallback pricing if API fails
        setPricingPlans([
          {
            name: "Free",
            price: { monthly: 0, yearly: 0 },
            description: "Basic features for individuals",
            features: [
              "Export up to 50 products per day",
              "Basic product information",
              "CSV export format",
              "Email support",
            ],
            buttonText: "Get Started",
            buttonLink: "/",
            highlighted: false,
          },
          {
            name: "Professional",
            price: { monthly: 14.99, yearly: 140 },
            description: "Advanced features for professionals",
            features: [
              "Export unlimited products",
              "Complete product information",
              "Multiple export formats",
              "Collection filtering",
              "Priority email support",
              "Advanced search capabilities",
            ],
            buttonText: "Get Pro",
            buttonLink: "/subscription",
            highlighted: true,
          },
          {
            name: "Business",
            price: { monthly: 29.99, yearly: 300 },
            description: "Complete solution for businesses",
            features: [
              "Everything in Pro plan",
              "Multiple user accounts",
              "API access",
              "Bulk export capabilities",
              "Custom data fields",
              "Dedicated support",
              "White label exports",
            ],
            buttonText: "Get Business",
            buttonLink: "/subscription",
            highlighted: false,
          },
        ])
      } finally {
        setLoading(false)
      }
    }
    getPricing()
  }, [])

  // Update button links based on billing cycle
  const getButtonLink = useCallback(
    (plan) => {
      if (plan.name === "Free") {
        return "/"
      }

      const amount = plan.price?.[billingCycle] ?? 0
      const planType = plan.name.toLowerCase()
      return `/payments?plan=${planType}&amount=${amount}&billing=${billingCycle}`
    },
    [billingCycle],
  )

  if (loading) {
    return (
      <div className="pricing-page" style={{ textAlign: "center", padding: "5rem 0" }}>
        Loading pricing plans...
      </div>
    )
  }

  return (
    <div className="pricing-page">
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="pricing-hero-container">
          <h1 className="pricing-hero-title">Simple, Transparent Pricing</h1>
          <p className="pricing-hero-description">Choose the plan that fits your needs. No hidden fees or surprises.</p>
          {error && <p style={{ color: "#f59e0b", fontSize: "0.875rem", marginTop: "1rem" }}>{error}</p>}

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span className={`billing-option ${billingCycle === "monthly" ? "active" : ""}`}>Monthly</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={billingCycle === "yearly"}
                onChange={() => setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly")}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className={`billing-option ${billingCycle === "yearly" ? "active" : ""}`}>
              Yearly <span className="save-badge">Save 20%</span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="pricing-plans-section">
        <div className="pricing-plans-container">
          <div className="pricing-plans">
            {pricingPlans.map((plan, index) => (
              <div className={`pricing-plan ${plan.highlighted ? "highlighted" : ""}`} key={index}>
                <div className="plan-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-price">
                    <span className="price-amount">${plan.price?.[billingCycle]?.toFixed(2) ?? "0.00"}</span>
                    <span className="price-cycle">{billingCycle === "monthly" ? "/month" : "/year"}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>
                <div className="plan-features">
                  <ul>
                    {plan.features?.map((feature, featureIndex) => (
                      <li key={featureIndex}>
                        <span className="feature-check">✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="plan-footer">
                  <Link to={getButtonLink(plan)} className="plan-button">
                    {plan.buttonText}
                  </Link>
                </div>
                {plan.highlighted && <div className="popular-badge">Most Popular</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq-section">
        <div className="pricing-faq-container">
          <h2 className="pricing-faq-title">Frequently Asked Questions</h2>
          <div className="pricing-faq-list">
            <div className="pricing-faq-item">
              <h3 className="pricing-faq-question">Can I upgrade or downgrade my plan later?</h3>
              <p className="pricing-faq-answer">
                Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll be charged the prorated
                difference. When downgrading, the new rate will apply at the start of your next billing cycle.
              </p>
            </div>
            <div className="pricing-faq-item">
              <h3 className="pricing-faq-question">Do you offer refunds?</h3>
              <p className="pricing-faq-answer">
                We offer a 14-day money-back guarantee for all paid plans. If you're not satisfied with our service, you
                can request a full refund within 14 days of your purchase.
              </p>
            </div>
            <div className="pricing-faq-item">
              <h3 className="pricing-faq-question">What payment methods do you accept?</h3>
              <p className="pricing-faq-answer">
                We accept all major credit cards, including Visa, Mastercard, American Express, and Discover. We also
                support PayPal payments.
              </p>
            </div>
            <div className="pricing-faq-item">
              <h3 className="pricing-faq-question">Is there a contract or commitment?</h3>
              <p className="pricing-faq-answer">
                No, there are no long-term contracts or commitments. You can cancel your subscription at any time
                without penalty.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Styling */}
      <style jsx="true">{`
        /* Pricing Hero Section */
        .pricing-hero {
          background-color: #007BFF;
          color: white;
          padding: 5rem 0;
          text-align: center;
        }
        
        .pricing-hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .pricing-hero-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }
        
        .pricing-hero-description {
          font-size: 1.25rem;
          max-width: 800px;
          margin: 0 auto 2rem;
          opacity: 0.9;
        }
        
        /* Billing Toggle */
        .billing-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 2rem;
        }
        
        .billing-option {
          font-size: 1rem;
          opacity: 0.7;
          transition: opacity 0.2s;
          display: flex;
          align-items: center;
        }
        
        .billing-option.active {
          opacity: 1;
          font-weight: 600;
        }
        
        .save-badge {
          background-color: #f33a3a;
          color: white;
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 1rem;
          margin-left: 0.5rem;
        }
        
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 60px;
          height: 30px;
        }
        
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.3);
          transition: 0.4s;
          border-radius: 34px;
        }
        
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 22px;
          width: 22px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          transition: 0.4s;
          border-radius: 50%;
        }
        
        input:checked + .toggle-slider {
          background-color: rgba(255, 255, 255, 0.3);
        }
        
        input:checked + .toggle-slider:before {
          transform: translateX(30px);
        }
        
        /* Pricing Plans */
        .pricing-plans-section {
          padding: 5rem 0;
          background-color: #f9fafb;
        }
        
        .pricing-plans-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .pricing-plans {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 2rem;
          margin-top: -3rem;
        }
        
        .pricing-plan {
          background-color: white;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          padding: 2rem;
          width: 100%;
          max-width: 350px;
          display: flex;
          flex-direction: column;
          position: relative;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .pricing-plan:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
        }
        
        .pricing-plan.highlighted {
          border: 2px solid #007BFF;
          transform: scale(1.05);
        }
        
        .pricing-plan.highlighted:hover {
          transform: scale(1.05) translateY(-5px);
        }
        
        .plan-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .plan-name {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #1a202c;
        }
        
        .plan-price {
          margin-bottom: 1rem;
        }
        
        .price-amount {
          font-size: 2.5rem;
          font-weight: 700;
          color: #007BFF;
        }
        
        .price-cycle {
          font-size: 1rem;
          color: #4a5568;
        }
        
        .plan-description {
          font-size: 1rem;
          color: #4a5568;
        }
        
        .plan-features {
          flex: 1;
          margin-bottom: 2rem;
        }
        
        .plan-features ul {
          list-style: none;
          padding: 0;
        }
        
        .plan-features li {
          margin-bottom: 0.75rem;
          color: #4a5568;
          display: flex;
          align-items: flex-start;
        }
        
        .feature-check {
          color: #007BFF;
          margin-right: 0.5rem;
          font-weight: bold;
        }
        
        .plan-footer {
          text-align: center;
        }
        
        .plan-button {
          display: inline-block;
          width: 100%;
          padding: 0.75rem 1.5rem;
          background-color: #007BFF;
          color: white;
          border-radius: 0.375rem;
          font-weight: 600;
          text-decoration: none;
          transition: background-color 0.2s;
        }
        
        .plan-button:hover {
          background-color: #008f85;
        }
        
        .popular-badge {
          position: absolute;
          top: -12px;
          right: 20px;
          background-color: #f33a3a;
          color: white;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
        }
        
        /* FAQ Section */
        .pricing-faq-section {
          padding: 5rem 0;
          background-color: white;
        }
        
        .pricing-faq-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .pricing-faq-title {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 3rem;
          text-align: center;
          color: #1a202c;
        }
        
        .pricing-faq-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
          gap: 2rem;
        }
        
        .pricing-faq-item {
          background-color: #f9fafb;
          padding: 1.5rem;
          border-radius: 0.5rem;
        }
        
        .pricing-faq-question {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: #1a202c;
        }
        
        .pricing-faq-answer {
          font-size: 1rem;
          color: #4a5568;
          line-height: 1.6;
        }
        
        /* Responsive Styles */
        @media (max-width: 1024px) {
          .pricing-plans {
            justify-content: center;
          }
          
          .pricing-plan {
            max-width: 100%;
            width: calc(50% - 1rem);
          }
          
          .pricing-faq-list {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 768px) {
          .pricing-hero-title {
            font-size: 2rem;
          }
          
          .pricing-hero-description {
            font-size: 1.125rem;
          }
          
          .pricing-plan {
            width: 100%;
          }
          
          .pricing-plan.highlighted {
            transform: none;
          }
          
          .pricing-plan.highlighted:hover {
            transform: translateY(-5px);
          }
          
          .pricing-faq-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </div>
  )
}

export default Pricing
